import React, { useState, useMemo } from "react";
import {
  Card,
  Collapse,
  Descriptions,
  Tag,
  Timeline,
  Table,
  Space,
  Alert,
  Divider,
  Typography,
  Tabs,
  Steps,
  Input,
  Empty,
  Badge,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  FileTextOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  SendOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Panel } = Collapse;
const { Title, Paragraph, Text } = Typography;

// Wrapt elk voorkomen van `query` in `text` met <mark>. Niet-string waarden (bv. getallen, JSX) worden ongewijzigd
// teruggegeven zodat deze functie veilig in elke render-plek gebruikt kan worden.
const highlightMatch = (text, query) => {
  if (!query || typeof text !== "string" || !text) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerText.indexOf(lowerQuery);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ backgroundColor: "#ffe58f", padding: 0 }}>{text.slice(idx, idx + query.length)}</mark>
      {highlightMatch(text.slice(idx + query.length), query)}
    </>
  );
};

const Documentation = () => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState([]);
  const [activeTab, setActiveTab] = useState("purchase");
  const [searchQuery, setSearchQuery] = useState("");

  const statusColors = {
    draft: "default",
    pending_confirmation: "gold",
    pending_approval: "volcano",
    pending_supplier_review: "gold",
    payment_pending: "orange",
    deposit_paid: "cyan",
    paid: "lime",
    ready_for_production: "geekblue",
    in_production: "blue",
    partially_shipped: "orange",
    shipped: "orange",
    received: "cyan",
    under_review: "purple",
    completed: "green",
    cancelled: "red",
    split: "magenta",
  };

  // Bijgewerkt 15-07-2026 op basis van een volledige code-audit (zie PURCHASE_FLOW_AUDIT.md in de root van de
  // projectmap). Deze tabel beschrijft de daadwerkelijk bereikbare stappen — niet elke ooit-ontworpen status haalt
  // de praktijk, zie de toelichting onder "Statussen die wel bestaan maar niet meer gebruikt worden" hieronder.
  const statusFlowData = [
    {
      status: "Draft",
      color: "default",
      description: "Order wordt samengesteld in het CMS",
      actions: "Producten toevoegen, leverancier selecteren, details invullen",
      nextStatus: "Pending Confirmation",
      trigger: "Bombyxx zet de order handmatig op 'Pending Confirmation' zodra deze naar de leverancier mag",
    },
    {
      status: "Pending Confirmation",
      color: "gold",
      description: "Wacht op bevestiging van leverancier",
      actions:
        "Leverancier vult ETD in (verplicht) en kan aantallen/prijzen/verpakkingen/dimensies aanpassen. CBM wordt in de praktijk niet afgedwongen, ook al toont het scherm het als verplicht veld.",
      nextStatus: "Pending Approval",
      trigger: "Leverancier klikt op 'Submit for Approval'",
    },
    {
      status: "Pending Approval",
      color: "volcano",
      description: "Bombyxx moet leverancier wijzigingen goedkeuren",
      actions: "Bombyxx controleert aantallen, prijzen en dimensies",
      nextStatus: "Ready for Production / Pending Supplier Review",
      trigger: "Bombyxx keurt goed, of past zelf aan en stuurt terug naar de leverancier",
    },
    {
      status: "Pending Supplier Review",
      color: "gold",
      description: "Leverancier moet Bombyxx aanpassingen reviewen",
      actions: "Leverancier controleert aangepaste aantallen",
      nextStatus: "Pending Approval",
      trigger: "Leverancier past aan en dient opnieuw in",
    },
    {
      status: "Ready for Production",
      color: "geekblue",
      description: "Order is goedgekeurd door beide partijen",
      actions:
        "Bombyxx regelt de (aan)betaling buiten deze statuswaarde om, via de factuur/betalingen op de verzending",
      nextStatus: "In Production",
      trigger:
        "Bombyxx zet de order handmatig op 'In Production' — dit is géén automatische overgang op basis van een apart betaalstatus-veld op de order (dat veld bestaat wel in de data, maar wordt nergens gelezen of geschreven)",
    },
    {
      status: "In Production",
      color: "blue",
      description: "Leverancier is bezig met productie. Triggert automatisch de Exact-order-aanmaak.",
      actions:
        "Leverancier maakt in het Supplier Portal één of meerdere verzendingen aan — los van de order zelf, en eventueel met producten uit meerdere orders gecombineerd",
      nextStatus: "Partially Shipped / Shipped",
      trigger:
        "Automatisch: zodra een verzending wordt aangemaakt/bijgewerkt, berekent een Cloud Function de orderstatus opnieuw",
    },
    {
      status: "Partially Shipped",
      color: "orange",
      description: "Een deel van de bestelde producten is verzonden, de rest nog niet",
      actions: "Wachten op de resterende verzending(en)",
      nextStatus: "Shipped",
      trigger: "Automatisch zodra alle producten in minstens één verzending zitten",
    },
    {
      status: "Shipped",
      color: "orange",
      description: "Alle producten van de order zijn verzonden",
      actions: "Bombyxx ontvangt en telt de verzending(en) op de verzending-detailpagina (zie sectie 7)",
      nextStatus: "Completed",
      trigger:
        "Bombyxx zet de order rechtstreeks op 'Completed' via de statusdropdown zodra alles is ontvangen en verwerkt",
    },
    {
      status: "Completed",
      color: "green",
      description: "Order is volledig afgerond",
      actions: "Geen acties meer mogelijk",
      nextStatus: "-",
      trigger: "-",
    },
  ];

  const emailNotifications = [
    {
      trigger: "PO Status: Draft → Pending Confirmation",
      recipient: "Leverancier",
      subject: "Action Required: Confirm Purchase Order",
      content: "Nieuwe order ontvangen, bevestig de order in het Supplier Portal",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: Pending Confirmation → Pending Approval",
      recipient: "Bombyxx",
      subject: "Actie vereist: Leverancier wijzigingen",
      content: "Leverancier heeft order bevestigd, controleer en keur goed",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: → In Production",
      recipient: "Bombyxx",
      subject: "Productie gestart",
      content: "Order gaat in productie + Exact sync (order aanmaken in Exact Online)",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: → Cancelled",
      recipient: "Leverancier",
      subject: "Purchase order cancelled",
      content: "Order is geannuleerd (geen email als dit vanuit Draft gebeurt)",
      category: "purchase_order",
    },
    {
      trigger: "Verzending aangemaakt (nieuwe shipment)",
      recipient: "Bombyxx",
      subject: "Pre-order update",
      content:
        "Als de verzending producten bevat zonder voorraad, worden die producten automatisch op pre-order gezet in de webshop (~45 dagen beschikbaarheid). Dit is ook het moment waarop de melding over 'order onderweg' feitelijk binnenkomt — er bestaat géén aparte 'Shipped'-email op de order zelf.",
      category: "purchase_order",
    },
    {
      trigger: "Shipment Status → Shipped / In Transit",
      recipient: "Bombyxx",
      subject: "Verzending {nr} is verzonden/onderweg",
      content: "Leverancier heeft verzending verzonden of is onderweg",
      category: "shipment",
    },
    {
      trigger: "Shipment Status → Received",
      recipient: "Leverancier + Bombyxx",
      subject: "Verzending {nr} is ontvangen",
      content: "Bombyxx heeft de verzending ontvangen, telling wordt uitgevoerd",
      category: "shipment",
    },
    {
      trigger: "Shipment Status → Completed",
      recipient: "Leverancier + Bombyxx",
      subject: "Verzending {nr} is geteld en afgerond",
      content: "Telling afgerond + Voorraadtelling (Stock Count) verwerkt in Exact via QuantityDifference",
      category: "shipment",
    },
    {
      trigger: "Na voorraadtelling: notify-accountant",
      recipient: "Boekhouder (Bombyxx intern)",
      subject: "Ontvangst verwerkt: Purchase Order {nr}",
      content:
        "Direct verstuurd door CMS via POST /api/exact/shipments/{id}/notify-accountant. Tijdstip zichtbaar als 'Mail boekhouder verstuurd' op shipment detail.",
      category: "shipment",
    },
    {
      trigger: "Invoice aangemaakt (onCreate)",
      recipient: "Bombyxx",
      subject: "Nieuwe factuur ontvangen: {nr}",
      content: "Leverancier heeft factuur ingediend + automatische sync naar Exact Online",
      category: "invoice",
    },
    {
      trigger: "Invoice Status → Approved",
      recipient: "Leverancier",
      subject: "Invoice {nr} has been approved",
      content: "Bombyxx heeft de factuur goedgekeurd",
      category: "invoice",
    },
    {
      trigger: "Invoice Status → Paid",
      recipient: "Leverancier",
      subject: "Invoice {nr} has been fully paid",
      content: "Factuur is volledig betaald",
      category: "invoice",
    },
    {
      trigger: "Invoice Status → Partially Paid",
      recipient: "Leverancier",
      subject: "Invoice {nr}: Partial payment received",
      content: "Gedeeltelijke betaling ontvangen voor factuur",
      category: "invoice",
    },
  ];

  const invoiceProcess = [
    {
      step: "1. Proforma Invoice (Optioneel, op PO-niveau)",
      description: "Leverancier kan een proforma invoice uploaden op de purchase order voordat productie start",
      fields: ["Proforma Invoice Number", "Proforma Invoice Date", "Proforma PDF"],
      timing: "Tijdens In Production status",
      location: "Supplier Portal - PO Detail - Invoice Modal - Proforma tab",
    },
    {
      step: "2. Verzending (Shipment) aanmaken",
      description: "Bombyxx maakt een verzending aan in het CMS die producten uit één of meerdere PO's bundelt",
      fields: ["Shipment Number", "Source Orders", "Items (per product: quantity)", "Tracking Code", "ETD"],
      timing: "Wanneer producten klaar zijn voor verzending",
      location: "CMS - Verzendingen",
    },
    {
      step: "3. Final Invoice (op verzending-niveau)",
      description: "Leverancier uploadt de definitieve factuur gekoppeld aan een verzending (niet meer aan PO)",
      fields: [
        "Invoice Number",
        "Invoice Date",
        "Invoice Lines (automatisch gegenereerd op basis van shipment items)",
        "Additional Costs (optioneel: mold costs, photoshoot, etc.)",
        "Remarks",
        "Invoice PDF",
      ],
      timing: "Na aanmaken verzending",
      location: "Supplier Portal - Shipment Detail - Invoice Modal",
    },
    {
      step: "4. Factuur Status",
      description: "Bombyxx beheert de factuurstatus in het CMS via de shipment detail pagina",
      fields: ["Draft", "Submitted (standaard bij aanmaken)", "Approved", "Partially Paid", "Paid", "Disputed"],
      timing: "Na ontvangst factuur",
      location: "CMS - Shipment Detail - Factuur sectie - Status dropdown",
    },
    {
      step: "5. Invoice Synchronisatie",
      description: "Invoice wordt automatisch gesynchroniseerd naar Exact Online",
      fields: ["Purchase Invoice ID wordt opgeslagen in Firestore (exactPurchaseInvoiceId)"],
      timing: "Automatisch via Cloud Function",
      location: "Exact Online - Purchase Invoices",
    },
  ];

  const paymentProcess = [
    {
      status: "Submitted (Ingediend)",
      description: "Factuur is aangemaakt door leverancier, wacht op betaling",
      action: "Bombyxx voegt betaling toe via 'Betaling toevoegen' op de shipment detail pagina",
      fields: [
        "Amount (USD)",
        "Payment Date",
        "Payment Method (bank, Wise, PayPal, etc.)",
        "Reference / Transaction ID",
        "Notes (optioneel)",
        "Payment Proof (PDF/afbeelding)",
      ],
    },
    {
      status: "Partially Paid (Deels betaald)",
      description: "Eén of meer betalingen gedaan, maar nog niet volledig betaald",
      calculation: "0 < totalPaid < totalAmount",
      action: "Bombyxx kan aanvullende betalingen toevoegen",
    },
    {
      status: "Paid (Betaald)",
      description: "Factuur is volledig betaald",
      calculation: "totalPaid ≥ totalAmount",
      trigger: "Status wordt automatisch 'Paid' bij volledige betaling",
    },
  ];

  const productMasterDataUpdate = [
    {
      trigger: "Order Creation",
      description: "Bij het aanmaken van een nieuwe purchase order",
      updates: ["Purchase Price", "Inner Packaging Quantity", "Master Packaging Quantity"],
      condition: "Als waarden verschillen van huidige product master data",
    },
    {
      trigger: "Approve Supplier Changes",
      description: "Wanneer Bombyxx leverancier wijzigingen goedkeurt",
      updates: ["Purchase Price", "Inner Packaging Quantity", "Master Packaging Quantity"],
      condition: "Als adjusted values verschillen van originele values",
      location: "CMS - Order Detail - 'Approve Changes' button",
    },
    {
      trigger: "Product Draft Opslaan (nieuwe producten)",
      description:
        "Wanneer leverancier details opslaat in het ProductDraftEditor formulier voor een nieuw product (product draft)",
      updates: [
        "Purchase Price → order.products[index].price",
        "Inner Packaging Quantity",
        "Master Packaging Quantity",
        "Product Dimensions",
        "totalPrice (herberekend over alle producten)",
        "totalProducts (herberekend over alle producten)",
      ],
      condition: "Altijd bij opslaan draft, mits waarden ingevuld zijn",
      location: "Supplier Portal - Order Detail - ProductDraftEditor modal",
    },
  ];

  const productDraftProcess = [
    {
      step: "1. Product Draft Aanmaken",
      description: "Leverancier maakt een nieuw product draft aan in het Supplier Portal",
      location: "Supplier Portal - Order Detail - Product toevoegen",
      fields: ["Product Name", "Product Code", "Barcode (optioneel)"],
      status: "Needs Supplier Input",
    },
    {
      step: "2. Product Details Invullen",
      description:
        "Leverancier vult alle product details in via het ProductDraftEditor formulier in het Supplier Portal. Bij het opslaan worden de ingevulde waarden direct gesynchroniseerd naar de orderregel in de purchase order, en wordt de totaalprijs van de order automatisch herberekend.",
      fields: [
        "Purchase Price (wordt direct naar order.products[index].price geschreven)",
        "Inner Packaging Quantity",
        "Master Packaging Quantity",
        "Dimensietype (LxBxH / HxDiameter / LxB)",
        "Afmetingen (Length, Width, Height, Diameter – afhankelijk van dimensietype)",
        "Color (optioneel)",
        "Product Images (optioneel)",
      ],
      action: "Leverancier klikt op 'Opslaan' — draft én orderregel worden tegelijk bijgewerkt",
      status: "Submitted",
    },
    {
      step: "3. Bombyxx Review",
      description: "Bombyxx controleert de product details in het CMS",
      location: "CMS - Product Drafts pagina",
      options: ["Approve: Product wordt goedgekeurd", "Reject: Wijzigingen vereist met feedback"],
      status: "Submitted → Approved / Changes Requested",
    },
    {
      step: "4. Changes Requested (optioneel)",
      description: "Als Bombyxx wijzigingen vraagt, moet leverancier aanpassen",
      action: "Leverancier past product details aan en submit opnieuw",
      status: "Changes Requested → Submitted",
    },
    {
      step: "5. Product Publiceren",
      description: "Na approval kan Bombyxx het product publiceren naar de catalog",
      action: "Bombyxx klikt op 'Publish' in Product Drafts detail modal",
      result: "Nieuw product wordt aangemaakt in products collection",
      status: "Approved → Published",
    },
    {
      step: "6. Purchase Order Update",
      description:
        "Na publicatie wordt de purchase order automatisch geüpdatet met het nieuwe product ID. Het draft product blijft zichtbaar in CMS en Supplier Portal totdat het product is gepubliceerd.",
      result:
        "publishedProductId wordt opgeslagen in de draft; productId in de orderregel wordt bijgewerkt naar het nieuwe catalog product",
    },
    {
      step: "7. Verzending & Factuur",
      description:
        "Draft producten kunnen worden meegenomen in een verzending zodra de prijs is ingevuld door de leverancier. De unitPrice in de shipment items wordt gevuld vanuit item.price op de orderregel (gesynchroniseerd bij stap 2). De factuurregels worden automatisch gegenereerd op basis van de shipment items.",
      result:
        "Verzending en factuur bevatten de correcte prijs, ook voor nieuwe producten die nog niet gepubliceerd zijn",
    },
  ];

  const productDraftStatuses = [
    {
      status: "Needs Supplier Input",
      color: "default",
      description: "Product draft is aangemaakt maar nog niet compleet",
      action: "Leverancier moet details invullen",
    },
    {
      status: "Submitted",
      color: "processing",
      description: "Leverancier heeft product details ingediend",
      action: "Bombyxx moet reviewen en goedkeuren/afwijzen",
    },
    {
      status: "Changes Requested",
      color: "warning",
      description: "Bombyxx heeft wijzigingen gevraagd",
      action: "Leverancier moet aanpassingen maken",
    },
    {
      status: "Approved",
      color: "success",
      description: "Bombyxx heeft product goedgekeurd",
      action: "Bombyxx kan product publiceren naar catalog",
    },
    {
      status: "Published",
      color: "green",
      description: "Product is gepubliceerd naar de catalog",
      action: "Geen acties meer mogelijk",
    },
  ];

  const alertNotificationData = [
    {
      scenario: "Significante aantalswijziging (≥25% verandering)",
      trigger: "Bombyxx of leverancier past hoeveelheid aan van een bestaand product in de order",
      threshold: "Wijziging ≥ 25% t.o.v. oorspronkelijk besteld aantal",
      recipient: "Bombyxx (intern)",
      template: "purchase-order-alert.ftl (aantalswijziging tabel)",
      emailType: "purchase_order_quantity_changed",
      tableColumns: ["Product", "Oud aantal", "Nieuw aantal", "Wijziging (% + kleur)"],
      extraInfo: "Reeds verzonden hoeveelheid wordt ook getoond per product",
      historyEntry: "Ja — elke product aanpassing wordt apart gelogd in history",
    },
    {
      scenario: "Dubbele bestelling waarschuwing",
      trigger: "Nieuwe inkooporder aangemaakt met producten die al in andere actieve orders staan",
      threshold: "Eén of meer producten komen voor in een andere open order",
      recipient: "Bombyxx (intern)",
      template: "purchase-order-alert.ftl (dubbele bestelling tabel)",
      emailType: "purchase_order_duplicate_warning",
      tableColumns: ["Product", "Nieuw besteld", "Open in andere orders (per order-nummer)", "Onderweg", "Totaal"],
      extraInfo: "Alle actieve orders (niet completed/cancelled) worden gecheckt",
      historyEntry: "Ja — order aanmaken wordt gelogd als initiële history entry",
    },
  ];

  const historyLogData = [
    {
      changeType: "order_created",
      label: "Order aangemaakt",
      color: "#52c41a",
      description:
        "Wordt gelogd op het moment dat de order voor het eerst aangemaakt wordt (onCreatePurchaseOrder trigger)",
      fields: ["orderNumber"],
    },
    {
      changeType: "status",
      label: "Statuswijziging",
      color: "#1890ff",
      description:
        "Wordt gelogd elke keer dat de orderstatus verandert via het CMS (handleUpdateOrderStatus, handleApproveChanges, handleSaveAndSendToSupplier)",
      fields: ["oldValue (vorige status)", "newValue (nieuwe status)"],
    },
    {
      changeType: "product_amount",
      label: "Aantalswijziging product",
      color: "#faad14",
      description: "Wordt gelogd wanneer het bestelde aantal (of adjusted amount) van een bestaand product verandert",
      fields: ["productId", "productName", "productCode", "oldValue", "newValue"],
    },
    {
      changeType: "product_price",
      label: "Prijswijziging product",
      color: "#faad14",
      description:
        "Wordt gelogd wanneer de prijs (of adjusted price) van een bestaand product verandert (drempel: > 0.001)",
      fields: ["productId", "productName", "productCode", "oldValue", "newValue"],
    },
    {
      changeType: "product_added",
      label: "Product toegevoegd",
      color: "#1890ff",
      description: "Wordt gelogd wanneer een product dat nog niet in de order stond wordt toegevoegd",
      fields: ["productId", "productName", "productCode", "newValue (hoeveelheid)"],
    },
    {
      changeType: "product_removed",
      label: "Product verwijderd",
      color: "#ff4d4f",
      description: "Wordt gelogd wanneer een product volledig uit de order wordt verwijderd",
      fields: ["productId", "productName", "productCode", "oldValue (hoeveelheid)"],
    },
  ];

  const cloudFunctions = [
    {
      name: "onCreatePurchaseOrder",
      collection: "purchaseOrders",
      event: "onCreate",
      description: "Reageert op het aanmaken van een nieuwe purchase order",
      actions: [
        "Controleert of producten in de nieuwe order al open staan in andere actieve orders — zo ja: waarschuwing (zie sectie 11)",
        "Telt hierbij ook onderweg-zijnde hoeveelheden mee (via purchaseOrderShipments)",
        "Schrijft de initiële 'order_created' geschiedenis-entry naar purchaseOrders/{id}/history",
      ],
    },
    {
      name: "onUpdatePurchaseOrder",
      collection: "purchaseOrders",
      event: "onUpdate",
      description: "Reageert op status- en productwijzigingen van purchase orders",
      actions: [
        "Email naar leverancier bij: pending_confirmation, cancelled",
        "Email naar Bombyxx bij: pending_approval, in_production",
        "Significante aantalswijziging notificatie naar Bombyxx (≥25% wijziging, kritiek bij ≥50% afname)",
        "Activiteitslog schrijven naar purchaseOrders/{id}/history sub-collectie",
        "Exact sync bij in_production: purchase order aanmaken in Exact Online",
      ],
    },
    {
      name: "onCreatePurchaseOrderShipment",
      collection: "purchaseOrderShipments",
      event: "onCreate",
      description: "Reageert op het aanmaken van een nieuwe verzending",
      actions: [
        "Pre-order logica: producten zonder voorraad worden op pre-order gezet in de webshop (~45 dagen beschikbaarheid)",
        "Email naar Bombyxx met een overzicht van de producten die op pre-order zijn gezet",
      ],
    },
    {
      name: "onUpdatePurchaseOrderShipment",
      collection: "purchaseOrderShipments",
      event: "onUpdate",
      description: "Reageert op status changes van verzendingen en werkt de order-status bij",
      actions: [
        "Email naar Bombyxx bij: shipped, in_transit",
        "Email naar leverancier + Bombyxx bij: received",
        "Email naar leverancier + Bombyxx bij: completed",
        "Berekent de order-status opnieuw (partially_shipped / shipped) op basis van alle verzendingen van de order",
        "Geen Exact sync via Cloud Function — voorraadtelling wordt direct via CMS → API verwerkt (zie sectie 7)",
      ],
    },
    {
      name: "onCreatePurchaseOrderInvoice",
      collection: "purchaseOrderInvoices",
      event: "onCreate",
      description: "Reageert op nieuwe facturen",
      actions: [
        "Roept de Exact-sync aan om de factuur als Purchase Invoice aan te maken — dit endpoint staat op dit moment bewust stil (zie sectie 3), er wordt dus momenteel geen factuur in Exact aangemaakt",
        "Email notificatie naar Bombyxx: nieuwe factuur ontvangen",
      ],
    },
    {
      name: "onUpdatePurchaseOrderInvoice",
      collection: "purchaseOrderInvoices",
      event: "onUpdate",
      description: "Reageert op factuur status changes",
      actions: ["Email naar leverancier bij: approved, paid, partially_paid"],
    },
  ];

  const shipmentProcess = [
    {
      step: "1. Verzending Aanmaken",
      description:
        "Leverancier maakt een verzending aan in het Supplier Portal, met producten uit één of meerdere purchase orders",
      fields: [
        "Shipment Number (automatisch)",
        "Source Orders",
        "Items per product: quantity",
        "Tracking Code (optioneel)",
        "ETD (optioneel)",
      ],
      timing: "Wanneer producten klaar zijn voor verzending",
      location: "Supplier Portal - Shipments - Create Shipment",
    },
    {
      step: "2. Verzending Onderweg",
      description: "Verzending wordt verzonden door leverancier. Status gaat naar 'shipped' of 'in_transit'",
      timing: "Na aanmaken verzending",
      location: "Supplier Portal / CMS - Shipment Detail",
    },
    {
      step: "3. Verzending Ontvangen",
      description: "Bombyxx ontvangt de verzending en zet status op 'received'",
      timing: "Bij fysieke ontvangst",
      location: "CMS - Shipment Detail - Status dropdown",
    },
    {
      step: "4. Kostprijs Controle (voor vreemde valuta)",
      description:
        "Voordat de telling begint, verschijnt automatisch een kostprijs controle modal als de verzending producten bevat met een vreemde valuta (USD, IDR, etc.)",
      fields: [
        "Wisselkoers (automatisch opgehaald uit purchase order, aanpasbaar)",
        "Per product: Inkoopprijs origineel, Nieuwe kostprijs EUR (automatisch berekend, aanpasbaar)",
        "Formule: Inkoopprijs × Wisselkoers = Kostprijs EUR",
      ],
      timing: "Bij openen 'Verwerk Ontvangst' modal",
      location: "CMS - Shipment Detail - Kostprijs modal (automatisch)",
      notification:
        "Na bevestiging worden de kostprijzen bijgewerkt in Exact Online via CostPriceNew op het logistics/Items endpoint (niet SupplierItem). History record wordt opgeslagen.",
    },
    {
      step: "5. Voorraadtelling (Stock Count)",
      description:
        "Bombyxx telt de ontvangen producten via de 'Verwerk Ontvangst' modal. De voorraad wordt verhoogd met de ontvangen aantallen via QuantityDifference — de bestaande voorraad wordt niet overschreven.",
      fields: ["Per product: Geteld/ontvangen aantal (editable input)", "Besteld aantal (read-only ter referentie)"],
      timing: "Na kostprijs controle (of direct bij EUR orders)",
      location: "CMS - Shipment Detail - '🔢 Voorraadtelling verwerken' knop",
    },
    {
      step: "6. Verwerking na Telling",
      description:
        "Na een succesvolle voorraadtelling worden de volgende acties direct door de CMS uitgevoerd (niet via Cloud Function):",
      fields: [
        "Stock count aangemaakt + verwerkt in Exact Online (QuantityDifference per artikel)",
        "exactStockCountId (Exact GUID) opgeslagen in Firestore op de shipment",
        "Shipment status → completed, receivedAt + receivedQuantities opgeslagen",
        "Mail naar boekhouder verstuurd via POST /api/exact/shipments/{id}/notify-accountant",
        "accountantMailSentAt (Unix timestamp) opgeslagen in Firestore op de shipment",
        "Email naar leverancier + Bombyxx via Cloud Function onUpdatePurchaseOrderShipment",
      ],
      timing: "Direct na klikken op '🔢 Voorraadtelling verwerken'",
      location: "CMS → Java Spring Boot API → Exact Online",
    },
  ];

  // Doorzoekbare platte tekst per sectie (paneel-key), gebruikt door de zoekbalk hierboven om te bepalen welke
  // secties automatisch open moeten klappen. Bevat de databronnen van elke sectie plus een paar extra trefwoorden
  // die alleen in de vaste (niet data-gedreven) tekst van die sectie voorkomen.
  const panelSearchText = useMemo(
    () => ({
      1: `Purchase Order Status Flow ${JSON.stringify(statusFlowData)} draft pending confirmation approval
        supplier review ready for production in production partially shipped completed cancelled split order
        splitsen niet gebouwd received under review onbereikbaar ETD CBM`,
      2: `Email Notificaties ${JSON.stringify(emailNotifications)} emailQueue scheduled sent failed retry
        pending_supplier_review payment_pending additional_costs_added purchase_order_shipped niet actief
        niet verstuurd`,
      3: `Facturatie Proces ${JSON.stringify(invoiceProcess)} proforma invoice factuur additional costs Exact
        Online exactPurchaseInvoiceId bewust uitgeschakeld 21-04-2026 stilgezet BTW invoice lines discount`,
      4: `Betalingsproces ${JSON.stringify(paymentProcess)} purchaseOrderPayments totalPaid amountDue submitted
        partially paid disputed aanbetaling restbetaling`,
      5: `Product Master Data Updates ${JSON.stringify(productMasterDataUpdate)} purchase price inner packaging
        master packaging approve supplier changes`,
      6: `Nieuwe Producten Product Drafts ${JSON.stringify(productDraftProcess)} ${JSON.stringify(
        productDraftStatuses,
      )} needs supplier input submitted changes requested approved published productDrafts`,
      7: `Verzendingen Goods Receipt Tellingen ${JSON.stringify(shipmentProcess)} stock count QuantityDifference
        CostPriceNew SupplierItem kostprijs wisselkoers notify-accountant boekhouder tracking ETD CBM shipment
        statussen draft shipped in transit received completed`,
      8: `Cloud Functions Automatische Triggers ${JSON.stringify(cloudFunctions)} onCreatePurchaseOrder
        onUpdatePurchaseOrder onCreatePurchaseOrderShipment onUpdatePurchaseOrderShipment
        onCreatePurchaseOrderInvoice onUpdatePurchaseOrderInvoice src/production src/development index.js
        Slack sendExceptionToSlack emailQueue`,
      9: `Systeem Architectuur Firestore Collections purchaseOrders purchaseOrderShipments purchaseOrderInvoices
        purchaseOrderPayments products suppliers productDrafts emailQueue config Supplier Portal Java Spring Boot
        Firebase Storage Exact Online data flow`,
      10: `Troubleshooting FAQ email niet verzonden invoice niet gesynchroniseerd exact order splitsen niet
        beschikbaar product master data niet geüpdatet additional costs voorraadtelling niet verwerkt shipment
        emails niet verzonden betaalstatus factuur`,
      11: `Automatische Meldingen Inkooporder Wijzigingen ${JSON.stringify(alertNotificationData)} dubbele
        bestelling aantalswijziging duplicate_warning quantity_changed drempel 25% purchase-order-alert.ftl`,
      12: `Activiteitslog Geschiedenis Inkooporder ${JSON.stringify(historyLogData)} history changedBy changedAt
        lastModifiedBy order_created status product_amount product_price product_added product_removed
        Firestore index`,
    }),
    [],
  );

  const matchingPanelKeys = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;
    return Object.keys(panelSearchText).filter((key) => panelSearchText[key].toLowerCase().includes(query));
  }, [searchQuery, panelSearchText]);

  const panelSectionTitles = {
    1: "Purchase Order Status Flow",
    2: "Email Notificaties",
    3: "Facturatie Proces",
    4: "Betalingsproces",
    5: "Product Master Data Updates",
    6: "Nieuwe Producten Toevoegen (Product Drafts)",
    7: "Verzendingen & Goods Receipt (Tellingen)",
    8: "Cloud Functions (Automatische Triggers)",
    9: "Systeem Architectuur",
    10: "Troubleshooting & FAQ",
    11: "Automatische Meldingen bij Inkooporder Wijzigingen",
    12: "Activiteitslog (Geschiedenis per inkooporder)",
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => <Tag color={record.color}>{highlightMatch(text, searchQuery)}</Tag>,
    },
    {
      title: "Beschrijving",
      dataIndex: "description",
      key: "description",
      render: (text) => highlightMatch(text, searchQuery),
    },
    {
      title: "Acties",
      dataIndex: "actions",
      key: "actions",
      render: (text) => highlightMatch(text, searchQuery),
    },
    {
      title: "Volgende Status",
      dataIndex: "nextStatus",
      key: "nextStatus",
      render: (text) => highlightMatch(text, searchQuery),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (text) => highlightMatch(text, searchQuery),
    },
  ];

  const emailColumns = [
    {
      title: "Bron",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text) => {
        const colors = { purchase_order: "blue", shipment: "orange", invoice: "purple" };
        const labels = { purchase_order: "Purchase Order", shipment: "Verzending", invoice: "Factuur" };
        return <Tag color={colors[text] || "default"}>{labels[text] || text}</Tag>;
      },
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (text) => highlightMatch(text, searchQuery),
    },
    {
      title: "Ontvanger",
      dataIndex: "recipient",
      key: "recipient",
      render: (text) => {
        if (text.includes("+")) {
          return text
            .split("+")
            .map((r) => r.trim())
            .map((r, i) => (
              <Tag key={i} color={r === "Bombyxx" ? "blue" : "green"} style={{ marginBottom: 2 }}>
                {r}
              </Tag>
            ));
        }
        return <Tag color={text === "Bombyxx" ? "blue" : "green"}>{text}</Tag>;
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => highlightMatch(text, searchQuery),
    },
    {
      title: "Inhoud",
      dataIndex: "content",
      key: "content",
      render: (text) => highlightMatch(text, searchQuery),
    },
  ];

  return (
    <Card>
      <Title level={2}>
        <FileTextOutlined /> Documentatie
      </Title>
      <Paragraph>
        Volledige documentatie voor het Bombyxx systeem, inclusief inkoopproces en marketing functionaliteiten.
      </Paragraph>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginTop: 24 }}
        items={[
          {
            key: "purchase",
            label: (
              <span>
                <ShoppingCartOutlined /> Inkoopproces
              </span>
            ),
            children: (
              <>
                <Paragraph>
                  Deze documentatie beschrijft het volledige inkoopproces van Bombyxx, inclusief alle statussen,
                  workflows, email notificaties en speciale processen zoals facturatie en verzendingen.
                </Paragraph>
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Laatst bijgewerkt op 15-07-2026"
                  description="Deze pagina beschrijft de daadwerkelijke, geverifieerde huidige werking — inclusief plekken waar een eerder ontworpen flow (zoals order splitsen, of de received/under review tussenstap) niet (meer) daadwerkelijk werkt. Voor de volledige analyse met risico's, opschoonpunten en een gefaseerde roadmap: zie PURCHASE_FLOW_AUDIT.md in de root van de projectmap."
                />

                {/* Flow in het kort — compacte samenvatting vóór de gedetailleerde secties hieronder */}
                <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
                  <Title level={4} style={{ marginTop: 0 }}>
                    Flow in het kort
                  </Title>
                  <Steps
                    size="small"
                    responsive
                    items={[
                      { title: "Draft", description: "Bombyxx stelt order samen" },
                      { title: "Pending Confirmation", description: "Leverancier bevestigt + ETD" },
                      { title: "Pending Approval", description: "Bombyxx keurt goed" },
                      { title: "Ready for Production", description: "Klaar, wacht op start" },
                      { title: "In Production", description: "Productie + verzending(en)" },
                      { title: "Shipped", description: "Onderweg, ontvangen, geteld" },
                      { title: "Completed", description: "Afgerond" },
                    ]}
                  />
                  <Paragraph style={{ marginTop: 16, marginBottom: 0, fontSize: 13, color: "#888" }}>
                    Vanaf elke status (behalve Completed) kan een order naar <Tag color="red">Cancelled</Tag>. Tussen
                    Pending Approval en Pending Supplier Review kan het een aantal keer heen-en-weer gaan. Verzendingen,
                    facturatie en betalingen lopen los van deze status — zie de kaarten hieronder. Volledige details met
                    alle uitzonderingen: sectie 1 "Purchase Order Status Flow".
                  </Paragraph>

                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
                    <Card size="small" style={{ flex: "1 1 220px", minWidth: 220 }} title="📦 Verzendingen">
                      <Text style={{ fontSize: 13 }}>
                        Losstaand van de order. Eén verzending kan producten uit meerdere orders bundelen. Bombyxx telt
                        de ontvangst en dat werkt de voorraad in Exact bij. <br />
                        <Text type="secondary">Sectie 7</Text>
                      </Text>
                    </Card>
                    <Card size="small" style={{ flex: "1 1 220px", minWidth: 220 }} title="🧾 Facturatie & betaling">
                      <Text style={{ fontSize: 13 }}>
                        Optioneel een pro forma factuur op de order (aanbetaling), en een definitieve factuur per
                        verzending. Betaalstatus zit op de factuur, niet op de order. <br />
                        <Text type="secondary">Secties 3 &amp; 4</Text>
                      </Text>
                    </Card>
                    <Card size="small" style={{ flex: "1 1 220px", minWidth: 220 }} title="🆕 Nieuwe producten">
                      <Text style={{ fontSize: 13 }}>
                        Leverancier kan een nog niet bestaand product als 'draft' toevoegen aan een order; na
                        goedkeuring publiceert Bombyxx het naar de catalogus. <br />
                        <Text type="secondary">Sectie 6</Text>
                      </Text>
                    </Card>
                  </div>
                </Card>

                {/* Zoekbalk — filtert en klapt automatisch de secties open die de zoekterm bevatten */}
                <Input
                  size="large"
                  allowClear
                  placeholder="Zoek in de documentatie, bv. 'kostprijs', 'split', 'exact', 'received'..."
                  prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ marginBottom: 8 }}
                />
                {searchQuery.trim() && (
                  <div style={{ marginBottom: 16 }}>
                    {matchingPanelKeys.length > 0 ? (
                      <Space wrap>
                        <Badge status="success" />
                        <Text type="secondary">
                          {matchingPanelKeys.length} van de {Object.keys(panelSectionTitles).length} secties bevatten "
                          {searchQuery.trim()}":
                        </Text>
                        {matchingPanelKeys.map((key) => (
                          <Tag key={key} color="blue">
                            {key}. {panelSectionTitles[key]}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={`Geen sectie gevonden voor "${searchQuery.trim()}" — probeer een ander zoekwoord`}
                      />
                    )}
                  </div>
                )}

                <Collapse activeKey={searchQuery.trim() ? matchingPanelKeys : activeKey} onChange={setActiveKey}>
                  {/* Status Flow */}
                  <Panel
                    header={
                      <Space>
                        <ShoppingCartOutlined />
                        <strong>1. Purchase Order Status Flow</strong>
                      </Space>
                    }
                    key="1"
                  >
                    <Alert
                      message="Complete Status Flow"
                      description="Dit is de normale flow van een purchase order van begin tot eind. Sommige statussen kunnen worden overgeslagen afhankelijk van het proces."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Table
                      dataSource={statusFlowData}
                      columns={columns}
                      pagination={false}
                      rowKey="status"
                      size="small"
                      bordered
                    />

                    <Divider />

                    <Title level={4}>Visuele Timeline</Title>
                    <Timeline mode="left">
                      <Timeline.Item color="gray" dot={<ClockCircleOutlined />}>
                        <strong>Draft</strong>
                        <br />
                        Order wordt samengesteld in CMS
                      </Timeline.Item>
                      <Timeline.Item color="gold" dot={<ExclamationCircleOutlined />}>
                        <strong>Pending Confirmation</strong>
                        <br />
                        Leverancier stelt ETD in en vult product details in
                      </Timeline.Item>
                      <Timeline.Item color="volcano" dot={<ExclamationCircleOutlined />}>
                        <strong>Pending Approval</strong>
                        <br />
                        Bombyxx controleert en keurt goed
                      </Timeline.Item>
                      <Timeline.Item color="geekblue" dot={<DollarOutlined />}>
                        <strong>Ready for Production</strong>
                        <br />
                        Order is goedgekeurd; (aan)betaling wordt afgehandeld via de factuur, niet via deze status
                      </Timeline.Item>
                      <Timeline.Item color="blue" dot={<ClockCircleOutlined />}>
                        <strong>In Production</strong>
                        <br />
                        Leverancier produceert en maakt verzending(en) aan
                      </Timeline.Item>
                      <Timeline.Item color="orange" dot={<InfoCircleOutlined />}>
                        <strong>Partially Shipped / Shipped</strong>
                        <br />
                        Wordt automatisch bijgewerkt zodra er verzendingen bestaan voor de order
                      </Timeline.Item>
                      <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                        <strong>Completed</strong>
                        <br />
                        Bombyxx zet de order rechtstreeks op afgerond zodra alle verzendingen zijn ontvangen en geteld
                      </Timeline.Item>
                    </Timeline>

                    <Alert
                      style={{ marginTop: 16 }}
                      type="warning"
                      showIcon
                      message="Statussen die wel bestaan maar niet meer bruikbaar zijn"
                      description={
                        <span>
                          <Tag color="cyan">Received</Tag> en <Tag color="purple">Under Review</Tag> waren ooit bedoeld
                          als tussenstappen ná Shipped (pakket fysiek ontvangen, daarna tellen/discrepanties
                          registreren, dán pas afronden). Ze staan niet meer in de statuskeuzelijst en er is geen enkele
                          knop of automatisme meer dat een order hierop zet — een order gaat in de praktijk direct van{" "}
                          <Tag color="orange">Shipped</Tag> naar <Tag color="green">Completed</Tag>. Wil je de
                          tussenstap met discrepantie-registratie terug, dan is dat een bewuste keuze om opnieuw te
                          bouwen (zie beslispunten in PURCHASE_FLOW_AUDIT.md).
                        </span>
                      }
                    />

                    <Divider />

                    <Title level={4}>Alternatieve Flows</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Pending Approval → Pending Supplier Review">
                        Bombyxx past aantallen aan en stuurt terug naar leverancier
                      </Descriptions.Item>
                      <Descriptions.Item label="Pending Supplier Review → Pending Approval">
                        Leverancier past aan en stuurt terug naar Bombyxx voor goedkeuring
                      </Descriptions.Item>
                      <Descriptions.Item label="Elke status → Cancelled">
                        Order kan op elk moment worden geannuleerd (behalve Completed)
                      </Descriptions.Item>
                      <Descriptions.Item label={<Tag color="magenta">Split</Tag>}>
                        Order splitsen (in twee aparte orders met eigen ETD) stond gepland, maar is nooit gebouwd. De
                        status-waarde en kleur bestaan nog in de code, maar er is geen enkele knop of actie die dit
                        daadwerkelijk uitvoert — je kunt een order dus niet splitsen, ondanks eventuele oudere
                        documentatie of schermteksten die dit suggereren.
                      </Descriptions.Item>
                    </Descriptions>
                  </Panel>

                  {/* Email Notifications */}
                  <Panel
                    header={
                      <Space>
                        <MailOutlined />
                        <strong>2. Email Notificaties</strong>
                      </Space>
                    }
                    key="2"
                  >
                    <Alert
                      message="Automatische Email Notificaties"
                      description="Alle emails worden automatisch verstuurd via Cloud Functions wanneer bepaalde triggers plaatsvinden. Emails worden in een queue geplaatst en binnen 1 minuut verzonden."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Table
                      dataSource={emailNotifications}
                      columns={emailColumns}
                      pagination={false}
                      rowKey="trigger"
                      size="small"
                      bordered
                    />

                    <Alert
                      style={{ marginTop: 16 }}
                      type="warning"
                      showIcon
                      message="Onderwerpen die wél gedefinieerd zijn, maar in de praktijk nooit verstuurd worden"
                      description={
                        <span>
                          In de code bestaan e-mailteksten voor <Text code>paid</Text>,{" "}
                          <Text code>payment_pending</Text>, <Text code>additional_costs_added</Text> en het
                          rechtstreeks op de order zetten van <Text code>completed</Text> / <Text code>received</Text> —
                          maar geen enkel huidig codepad triggert ze nog. Als je een van deze meldingen zou verwachten
                          en hem niet krijgt, is dat dus geen storing: het is een e-mailtype dat nog niet (opnieuw) is
                          aangesloten.
                        </span>
                      }
                    />

                    <Divider />

                    <Title level={4}>Email Queue Systeem</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Collection">emailQueue (Firestore)</Descriptions.Item>
                      <Descriptions.Item label="Processing">
                        Cloud Function checkt elke minuut op nieuwe emails
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">scheduled → sent / failed</Descriptions.Item>
                      <Descriptions.Item label="Retry Logic">
                        Max 3 retries bij failures, 1 uur tussen retries
                      </Descriptions.Item>
                      <Descriptions.Item label="Email Service">
                        Java Spring Boot API (MarketingService)
                      </Descriptions.Item>
                    </Descriptions>
                  </Panel>

                  {/* Invoice Process */}
                  <Panel
                    header={
                      <Space>
                        <FileTextOutlined />
                        <strong>3. Facturatie Proces</strong>
                      </Space>
                    }
                    key="3"
                  >
                    <Alert
                      message="Invoice Workflow"
                      description="Leveranciers kunnen zowel een proforma invoice als een final invoice uploaden. Additional costs kunnen worden toegevoegd voor extra kosten zoals mold costs of photoshoot kosten."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Timeline>
                      {invoiceProcess.map((item, index) => (
                        <Timeline.Item key={index} color="blue">
                          <Title level={5}>{highlightMatch(item.step, searchQuery)}</Title>
                          <Paragraph>{highlightMatch(item.description, searchQuery)}</Paragraph>
                          {item.fields && (
                            <>
                              <Text strong>Velden:</Text>
                              <ul>
                                {item.fields.map((field, idx) => (
                                  <li key={idx}>{highlightMatch(field, searchQuery)}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {item.timing && (
                            <Paragraph>
                              <Text strong>Timing:</Text> {highlightMatch(item.timing, searchQuery)}
                            </Paragraph>
                          )}
                          {item.location && (
                            <Paragraph>
                              <Text strong>Locatie:</Text> {highlightMatch(item.location, searchQuery)}
                            </Paragraph>
                          )}
                          {item.notification && (
                            <Alert message={item.notification} type="warning" showIcon style={{ marginTop: 8 }} />
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>

                    <Divider />

                    <Title level={4}>Invoice Line Items</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Automatische Generatie">
                        Invoice lines worden automatisch gegenereerd op basis van shipment items (producten in de
                        verzending)
                      </Descriptions.Item>
                      <Descriptions.Item label="Velden per Line">
                        Product ID, Product Name, Color, Quantity, Unit Price, Discount %, Total Price
                      </Descriptions.Item>
                      <Descriptions.Item label="Berekening">
                        Total Price = Quantity × Unit Price × (1 - Discount% / 100)
                      </Descriptions.Item>
                      <Descriptions.Item label="Collectie">
                        purchaseOrderInvoices (standalone document, gekoppeld aan shipmentId)
                      </Descriptions.Item>
                      <Descriptions.Item label="Factuuroverzicht">
                        CMS - Inkoop - Inkoopfacturen: tabel met alle facturen, filters op leverancier/status/datum,
                        kolommen voor status/betaald/openstaand
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Additional Costs</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Voorbeelden">
                        Mold costs, Photoshoot kosten, Sample kosten, Shipping kosten
                      </Descriptions.Item>
                      <Descriptions.Item label="Velden">
                        Description, Amount, Currency, Quantity (optioneel), Unit Price (optioneel), Remarks
                      </Descriptions.Item>
                      <Descriptions.Item label="Email Notificatie">
                        Gedefinieerd, maar op dit moment niet actief — er wordt geen e-mail verstuurd wanneer additional
                        costs worden toegevoegd (zie de kanttekening in sectie 2)
                      </Descriptions.Item>
                      <Descriptions.Item label="Weergave in CMS">
                        Total Price toont breakdown: Producten + Extra Kosten = Totaal
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Invoice Synchronisatie naar Exact Online</Title>
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Op dit moment bewust uitgeschakeld (sinds 21-04-2026)"
                      description="Het aanmaken van inkoopfacturen in Exact Online staat stil. De Cloud Function roept het endpoint nog wel aan en krijgt een lege bevestiging terug (geen foutmelding), maar er wordt geen factuur aangemaakt in Exact. Dit is een bewuste, tijdelijke keuze — geen storing. Zie PURCHASE_FLOW_AUDIT.md voor de achtergrond."
                    />
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Trigger (zou moeten)">
                        Automatisch via Cloud Function wanneer een factuur wordt aangemaakt
                      </Descriptions.Item>
                      <Descriptions.Item label="API Endpoint">
                        POST /api/exact/purchase-orders/{"{purchaseOrderNumber}"}/invoice
                      </Descriptions.Item>
                      <Descriptions.Item label="Huidige status">
                        Endpoint retourneert een lege bevestiging zonder een factuur in Exact aan te maken
                      </Descriptions.Item>
                      <Descriptions.Item label="Exact ID">
                        exactPurchaseInvoiceId wordt daardoor momenteel niet gevuld
                      </Descriptions.Item>
                      <Descriptions.Item label="Status Indicator">
                        CMS toont "✓ Synced to Exact" alleen als exactPurchaseInvoiceId is gevuld — dit zal voor nieuwe
                        facturen dus niet verschijnen zolang de sync stilstaat
                      </Descriptions.Item>
                    </Descriptions>
                  </Panel>

                  {/* Payment Process */}
                  <Panel
                    header={
                      <Space>
                        <DollarOutlined />
                        <strong>4. Betalingsproces</strong>
                      </Space>
                    }
                    key="4"
                  >
                    <Alert
                      message="Payment Status Systeem"
                      description="Betalingen worden apart bijgehouden van de order status. Een order kan meerdere betalingen hebben (aanbetaling + restbetaling)."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Timeline>
                      {paymentProcess.map((item, index) => (
                        <Timeline.Item key={index} color="green">
                          <Title level={5}>{highlightMatch(item.status, searchQuery)}</Title>
                          <Paragraph>{highlightMatch(item.description, searchQuery)}</Paragraph>
                          {item.action && (
                            <Paragraph>
                              <Text strong>Actie:</Text> {highlightMatch(item.action, searchQuery)}
                            </Paragraph>
                          )}
                          {item.fields && (
                            <>
                              <Text strong>Velden:</Text>
                              <ul>
                                {item.fields.map((field, idx) => (
                                  <li key={idx}>{highlightMatch(field, searchQuery)}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {item.calculation && (
                            <Paragraph>
                              <Text strong>Berekening:</Text> {highlightMatch(item.calculation, searchQuery)}
                            </Paragraph>
                          )}
                          {item.trigger && (
                            <Alert message={item.trigger} type="success" showIcon style={{ marginTop: 8 }} />
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>

                    <Divider />

                    <Title level={4}>Payment Berekeningen</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Collectie">
                        purchaseOrderPayments (apart document per betaling, gekoppeld aan invoiceId)
                      </Descriptions.Item>
                      <Descriptions.Item label="Total to Pay">
                        Invoice totalAmount (producten + additional costs)
                      </Descriptions.Item>
                      <Descriptions.Item label="Total Paid">
                        Som van alle payment amounts (opgeslagen als totalPaid op de factuur)
                      </Descriptions.Item>
                      <Descriptions.Item label="Amount Due">
                        totalAmount - totalPaid (opgeslagen als amountDue op de factuur)
                      </Descriptions.Item>
                      <Descriptions.Item label="Automatische Status Logic">
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>Submitted: Geen betalingen (totalPaid = 0)</li>
                          <li>Partially Paid: 0 &lt; totalPaid &lt; totalAmount</li>
                          <li>Paid: totalPaid ≥ totalAmount</li>
                        </ul>
                      </Descriptions.Item>
                      <Descriptions.Item label="Locatie">
                        CMS - Shipment Detail - Factuur sectie - 'Betaling toevoegen' knop
                      </Descriptions.Item>
                      <Descriptions.Item label="Supplier Portal">
                        Leverancier ziet betalingen read-only op de shipment detail pagina
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Factuur Status Overzicht</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Draft">Concept factuur, nog niet ingediend</Descriptions.Item>
                      <Descriptions.Item label="Submitted">
                        Ingediend door leverancier (standaard bij aanmaken)
                      </Descriptions.Item>
                      <Descriptions.Item label="Approved">Goedgekeurd door Bombyxx</Descriptions.Item>
                      <Descriptions.Item label="Partially Paid">
                        Deels betaald (automatisch bij eerste betaling)
                      </Descriptions.Item>
                      <Descriptions.Item label="Paid">
                        Volledig betaald (automatisch wanneer totalPaid ≥ totalAmount)
                      </Descriptions.Item>
                      <Descriptions.Item label="Disputed">Betwist door Bombyxx</Descriptions.Item>
                    </Descriptions>
                  </Panel>

                  {/* Product Master Data */}
                  <Panel
                    header={
                      <Space>
                        <InfoCircleOutlined />
                        <strong>5. Product Master Data Updates</strong>
                      </Space>
                    }
                    key="5"
                  >
                    <Alert
                      message="Automatische Product Updates"
                      description="Wanneer leveranciers prijzen of verpakkingen wijzigen in een purchase order, worden deze automatisch doorgevoerd in de product master data."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Timeline>
                      {productMasterDataUpdate.map((item, index) => (
                        <Timeline.Item key={index} color="cyan">
                          <Title level={5}>{highlightMatch(item.trigger, searchQuery)}</Title>
                          <Paragraph>{highlightMatch(item.description, searchQuery)}</Paragraph>
                          <Paragraph>
                            <Text strong>Updates:</Text>
                          </Paragraph>
                          <ul>
                            {item.updates.map((update, idx) => (
                              <li key={idx}>{highlightMatch(update, searchQuery)}</li>
                            ))}
                          </ul>
                          <Paragraph>
                            <Text strong>Conditie:</Text> {highlightMatch(item.condition, searchQuery)}
                          </Paragraph>
                          {item.location && (
                            <Paragraph>
                              <Text strong>Locatie:</Text> {highlightMatch(item.location, searchQuery)}
                            </Paragraph>
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>

                    <Divider />

                    <Title level={4}>Update Logica</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Vergelijking">
                        Order product waarden worden vergeleken met huidige product master data
                      </Descriptions.Item>
                      <Descriptions.Item label="Update Trigger">
                        Alleen als waarden verschillen wordt product master data geüpdatet
                      </Descriptions.Item>
                      <Descriptions.Item label="Metadata">
                        updatedAt (timestamp) en updatedBy (email) worden toegevoegd
                      </Descriptions.Item>
                      <Descriptions.Item label="Firestore Collection">products</Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Voorbeeld Scenario</Title>
                    <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
                      <Paragraph>
                        <Text strong>Product Master Data (voor order):</Text>
                      </Paragraph>
                      <ul>
                        <li>Purchase Price: $10.00</li>
                        <li>Inner Packaging: 12 stuks</li>
                        <li>Master Packaging: 144 stuks</li>
                      </ul>

                      <Divider />

                      <Paragraph>
                        <Text strong>Leverancier wijzigt in order:</Text>
                      </Paragraph>
                      <ul>
                        <li>Adjusted Price: $9.50</li>
                        <li>Adjusted Inner Packaging: 10 stuks</li>
                        <li>Adjusted Master Packaging: 120 stuks</li>
                      </ul>

                      <Divider />

                      <Paragraph>
                        <Text strong>Na Bombyxx Approval:</Text>
                      </Paragraph>
                      <ul>
                        <li>Purchase Price: $9.50 ✓ Updated</li>
                        <li>Inner Packaging: 10 stuks ✓ Updated</li>
                        <li>Master Packaging: 120 stuks ✓ Updated</li>
                        <li>updatedAt: 1737554400</li>
                        <li>updatedBy: sam@digitalkiss.nl</li>
                      </ul>
                    </Card>
                  </Panel>

                  {/* Product Drafts */}
                  <Panel
                    header={
                      <Space>
                        <PlusOutlined />
                        <strong>6. Nieuwe Producten Toevoegen (Product Drafts)</strong>
                      </Space>
                    }
                    key="6"
                  >
                    <Alert
                      message="Product Draft Workflow"
                      description="Wanneer een leverancier een nieuw product wil toevoegen dat nog niet in de catalog staat, wordt een Product Draft aangemaakt. Dit doorloopt een goedkeuringsproces voordat het wordt gepubliceerd."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Timeline>
                      {productDraftProcess.map((item, index) => (
                        <Timeline.Item key={index} color="blue">
                          <Title level={5}>{highlightMatch(item.step, searchQuery)}</Title>
                          <Paragraph>{highlightMatch(item.description, searchQuery)}</Paragraph>
                          {item.location && (
                            <Paragraph>
                              <Text strong>Locatie:</Text> {highlightMatch(item.location, searchQuery)}
                            </Paragraph>
                          )}
                          {item.fields && (
                            <>
                              <Text strong>Velden:</Text>
                              <ul>
                                {item.fields.map((field, idx) => (
                                  <li key={idx}>{highlightMatch(field, searchQuery)}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {item.options && (
                            <>
                              <Text strong>Opties:</Text>
                              <ul>
                                {item.options.map((option, idx) => (
                                  <li key={idx}>{highlightMatch(option, searchQuery)}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {item.action && (
                            <Paragraph>
                              <Text strong>Actie:</Text> {highlightMatch(item.action, searchQuery)}
                            </Paragraph>
                          )}
                          {item.result && (
                            <Paragraph>
                              <Text strong>Resultaat:</Text> {highlightMatch(item.result, searchQuery)}
                            </Paragraph>
                          )}
                          {item.status && (
                            <Tag color="blue" style={{ marginTop: 8 }}>
                              {item.status}
                            </Tag>
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>

                    <Divider />

                    <Title level={4}>Product Draft Statussen</Title>
                    <Table
                      dataSource={productDraftStatuses}
                      columns={[
                        {
                          title: "Status",
                          dataIndex: "status",
                          key: "status",
                          render: (text, record) => <Tag color={record.color}>{text}</Tag>,
                        },
                        {
                          title: "Beschrijving",
                          dataIndex: "description",
                          key: "description",
                        },
                        {
                          title: "Actie",
                          dataIndex: "action",
                          key: "action",
                        },
                      ]}
                      pagination={false}
                      rowKey="status"
                      size="small"
                      bordered
                    />

                    <Divider />

                    <Title level={4}>Belangrijke Details</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Firestore Collection">productDrafts</Descriptions.Item>
                      <Descriptions.Item label="Revisions">
                        Elke status wijziging wordt opgeslagen in een revisions subcollection
                      </Descriptions.Item>
                      <Descriptions.Item label="Purchase Order Koppeling">
                        Product draft wordt gekoppeld aan purchase order via purchaseOrderId en
                        purchaseOrderProductIndex
                      </Descriptions.Item>
                      <Descriptions.Item label="Directe Order Sync">
                        Bij elke opslag van de draft worden price, innerPackagingQuantity, masterPackagingQuantity en
                        productDimensions direct geschreven naar de bijbehorende orderregel
                        (products[purchaseOrderProductIndex]). Tegelijk worden totalPrice en totalProducts op de order
                        herberekend.
                      </Descriptions.Item>
                      <Descriptions.Item label="Quantity (aantallen)">
                        Quantity is read-only in het ProductDraftEditor formulier. De aantallen worden uitsluitend
                        beheerd door Bombyxx via het CMS (single source of truth: order.products[index].amount).
                      </Descriptions.Item>
                      <Descriptions.Item label="CMS Weergave">
                        CMS toont draft waarden (prijs, dimensies, verpakking) uit de productDrafts collectie. Als
                        item.price al gezet is op de orderregel, wordt die gebruikt; anders wordt de draft purchasePrice
                        als fallback getoond.
                      </Descriptions.Item>
                      <Descriptions.Item label="Published Product">
                        Na publicatie wordt publishedProductId opgeslagen in de draft
                      </Descriptions.Item>
                      <Descriptions.Item label="Existing Product Updates">
                        Als een draft een publishedProductId heeft, worden wijzigingen doorgevoerd in het bestaande
                        product
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Voorbeeld Scenario</Title>
                    <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
                      <Paragraph>
                        <Text strong>Stap 1: Leverancier maakt nieuw product aan</Text>
                      </Paragraph>
                      <ul>
                        <li>Product Name: "Bamboo Water Bottle"</li>
                        <li>Product Code: "BWB-001"</li>
                        <li>Barcode: "8719327001234"</li>
                        <li>Status: Needs Supplier Input</li>
                      </ul>

                      <Divider />

                      <Paragraph>
                        <Text strong>Stap 2: Leverancier vult details in</Text>
                      </Paragraph>
                      <ul>
                        <li>Purchase Price: $8.50</li>
                        <li>Inner Packaging: 12 stuks</li>
                        <li>Master Packaging: 144 stuks</li>
                        <li>Length: 25 cm</li>
                        <li>Diameter: 7 cm</li>
                        <li>Color: Natural Bamboo</li>
                        <li>Images: 3 product foto's geüpload</li>
                        <li>Status: Submitted</li>
                      </ul>

                      <Divider />

                      <Paragraph>
                        <Text strong>Stap 3: Bombyxx keurt goed</Text>
                      </Paragraph>
                      <ul>
                        <li>Reviewed by: sam@digitalkiss.nl</li>
                        <li>Status: Approved</li>
                      </ul>

                      <Divider />

                      <Paragraph>
                        <Text strong>Stap 4: Bombyxx publiceert</Text>
                      </Paragraph>
                      <ul>
                        <li>Nieuw product aangemaakt in products collection</li>
                        <li>Product ID: abc123xyz</li>
                        <li>Purchase order geüpdatet met productId</li>
                        <li>Status: Published</li>
                      </ul>
                    </Card>
                  </Panel>

                  {/* Shipment & Goods Receipt Process */}
                  <Panel
                    header={
                      <Space>
                        <SendOutlined />
                        <strong>7. Verzendingen & Goods Receipt (Tellingen)</strong>
                      </Space>
                    }
                    key="7"
                  >
                    <Alert
                      message="Verzendingen & Tellingen"
                      description="Verzendingen zijn losgekoppeld van purchase orders. Een verzending kan producten uit meerdere PO's bevatten. Na ontvangst worden producten geteld en automatisch verwerkt in Exact Online."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Timeline>
                      {shipmentProcess.map((item, index) => (
                        <Timeline.Item key={index} color={index === shipmentProcess.length - 1 ? "green" : "blue"}>
                          <Title level={5}>{highlightMatch(item.step, searchQuery)}</Title>
                          <Paragraph>{highlightMatch(item.description, searchQuery)}</Paragraph>
                          {item.fields && (
                            <>
                              <Text strong>Velden / Acties:</Text>
                              <ul>
                                {item.fields.map((field, idx) => (
                                  <li key={idx}>{highlightMatch(field, searchQuery)}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {item.timing && (
                            <Paragraph>
                              <Text strong>Timing:</Text> {highlightMatch(item.timing, searchQuery)}
                            </Paragraph>
                          )}
                          {item.location && (
                            <Paragraph>
                              <Text strong>Locatie:</Text> {highlightMatch(item.location, searchQuery)}
                            </Paragraph>
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>

                    <Divider />

                    <Title level={4}>Shipment Statussen</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label={<Tag color="default">Draft</Tag>}>
                        Verzending is aangemaakt maar nog niet verzonden
                      </Descriptions.Item>
                      <Descriptions.Item label={<Tag color="orange">Shipped</Tag>}>
                        Verzending is verzonden door leverancier
                      </Descriptions.Item>
                      <Descriptions.Item label={<Tag color="blue">In Transit</Tag>}>
                        Verzending is onderweg
                      </Descriptions.Item>
                      <Descriptions.Item label={<Tag color="cyan">Received</Tag>}>
                        Verzending is ontvangen bij Bombyxx - klaar voor telling
                      </Descriptions.Item>
                      <Descriptions.Item label={<Tag color="green">Completed</Tag>}>
                        Telling afgerond, voorraadtelling (stock count) verwerkt in Exact via QuantityDifference
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Voorraadtelling (Stock Count) naar Exact Online</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Trigger">
                        Bombyxx klikt op '🔢 Voorraadtelling verwerken' in de 'Verwerk Ontvangst' modal
                      </Descriptions.Item>
                      <Descriptions.Item label="Initiator">
                        CMS (direct) → Java Spring Boot API → Exact Online. Niet via Cloud Function.
                      </Descriptions.Item>
                      <Descriptions.Item label="API Endpoint">
                        POST /api/exact/shipments/{"{shipmentId}"}/stock-count
                      </Descriptions.Item>
                      <Descriptions.Item label="Body">
                        receiptDate + lines (itemCode, orderedAmount, receivedAmount)
                      </Descriptions.Item>
                      <Descriptions.Item label="Exact Verwerking">
                        <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>StockCount aanmaken in Exact (status draft) met QuantityDifference per artikel</li>
                          <li>ProcessStockCount aanroepen om de telling te bevestigen (status confirmed)</li>
                          <li>
                            Voorraad wordt verhoogd met ontvangen aantallen — bestaande voorraad niet overschreven
                          </li>
                        </ol>
                      </Descriptions.Item>
                      <Descriptions.Item label="Exact Description">
                        "Ontvangst verzending {"{shipmentNumber}"}" — gebruikt het leesbare shipment nummer (opgehaald
                        uit Firestore)
                      </Descriptions.Item>
                      <Descriptions.Item label="Firestore velden (op shipment)">
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>
                            <Text code>exactStockCountId</Text> — Exact GUID van de stock count
                          </li>
                          <li>
                            <Text code>exactStockCountSyncedAt</Text> — Unix timestamp van synchronisatie
                          </li>
                          <li>
                            <Text code>exactStockCountStatus</Text> — "synced"
                          </li>
                          <li>
                            <Text code>receivedQuantities</Text> — ontvangen aantallen per product
                          </li>
                          <li>
                            <Text code>accountantMailSentAt</Text> — Unix timestamp wanneer boekhouder mail verstuurd
                          </li>
                        </ul>
                      </Descriptions.Item>
                      <Descriptions.Item label="Boekhouder Mail">
                        Direct na telling: POST /api/exact/shipments/{"{shipmentId}"}/notify-accountant → Tijdstip
                        zichtbaar in shipment detail als 'Mail boekhouder verstuurd'
                      </Descriptions.Item>
                      <Descriptions.Item label="Kostprijs Update">
                        Via PUT naar logistics/Items endpoint met veld CostPriceNew (niet via SupplierItem)
                      </Descriptions.Item>
                    </Descriptions>
                  </Panel>

                  {/* Cloud Functions */}
                  <Panel
                    header={
                      <Space>
                        <ThunderboltOutlined />
                        <strong>8. Cloud Functions (Automatische Triggers)</strong>
                      </Space>
                    }
                    key="8"
                  >
                    <Alert
                      message="Firebase Cloud Functions"
                      description="Cloud Functions draaien automatisch op de achtergrond wanneer data in Firestore wijzigt. Ze verzorgen email notificaties, Exact Online synchronisatie, en voorraad updates."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    {cloudFunctions.map((cf, index) => (
                      <Card
                        key={index}
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={
                          <Space>
                            <Tag color="blue">{cf.event}</Tag>
                            <Text strong>{highlightMatch(cf.name, searchQuery)}</Text>
                            <Text type="secondary">→ {cf.collection}</Text>
                          </Space>
                        }
                      >
                        <Paragraph>{highlightMatch(cf.description, searchQuery)}</Paragraph>
                        <Text strong>Acties:</Text>
                        <ul>
                          {cf.actions.map((action, idx) => (
                            <li key={idx}>{highlightMatch(action, searchQuery)}</li>
                          ))}
                        </ul>
                      </Card>
                    ))}

                    <Divider />

                    <Title level={4}>Complete Flow Diagram</Title>
                    <Timeline>
                      <Timeline.Item color="blue">
                        <strong>1. Purchase Order → status: in_production</strong>
                        <br />
                        Cloud Function: Exact sync (order aanmaken in Exact)
                      </Timeline.Item>
                      <Timeline.Item color="orange">
                        <strong>2. Verzending aangemaakt</strong>
                        <br />
                        Cloud Function: pre-order logica voor producten zonder voorraad + email naar Bombyxx; de
                        order-status wordt bijgewerkt naar partially_shipped/shipped
                      </Timeline.Item>
                      <Timeline.Item color="cyan">
                        <strong>3. Verzending → status: received</strong>
                        <br />
                        Cloud Function: Email naar leverancier + Bombyxx
                      </Timeline.Item>
                      <Timeline.Item color="green">
                        <strong>4. CMS: Voorraadtelling invullen → Verzending status: completed</strong>
                        <br />
                        CMS → API (niet via Cloud Function): Stock Count aanmaken + verwerken in Exact
                        (QuantityDifference) + boekhouder email
                        <br />
                        Cloud Function: Email notificatie naar leverancier + Bombyxx
                      </Timeline.Item>
                      <Timeline.Item color="purple">
                        <strong>5. Factuur aangemaakt (gekoppeld aan verzending)</strong>
                        <br />
                        Cloud Function: probeert Exact invoice sync + email naar Bombyxx — de Exact-sync staat op dit
                        moment bewust stil (zie sectie 3), de email gaat wel gewoon uit
                      </Timeline.Item>
                      <Timeline.Item color="green">
                        <strong>6. Factuur → status: paid</strong>
                        <br />
                        Cloud Function: Email naar leverancier
                      </Timeline.Item>
                    </Timeline>

                    <Divider />

                    <Title level={4}>Bestanden</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Environment-switch">
                        cloud-functions/index.js — kiest op basis van het Firebase project ID welke omgeving geladen
                        wordt
                      </Descriptions.Item>
                      <Descriptions.Item label="Development">
                        cloud-functions/src/development/index.js
                      </Descriptions.Item>
                      <Descriptions.Item label="Production">cloud-functions/src/production/index.js</Descriptions.Item>
                      <Descriptions.Item label="Email Queue">
                        emailQueue collectie in Firestore - emails worden binnen 1 minuut verwerkt
                      </Descriptions.Item>
                      <Descriptions.Item label="Error Handling">
                        Fouten worden gelogd naar Slack via sendExceptionToSlack() — niet naar e-mail
                      </Descriptions.Item>
                    </Descriptions>
                  </Panel>

                  {/* System Architecture */}
                  <Panel
                    header={
                      <Space>
                        <InfoCircleOutlined />
                        <strong>9. Systeem Architectuur</strong>
                      </Space>
                    }
                    key="9"
                  >
                    <Title level={4}>Componenten</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="CMS (Bombyxx)">
                        React applicatie voor Bombyxx medewerkers om orders te beheren
                      </Descriptions.Item>
                      <Descriptions.Item label="Supplier Portal">
                        Next.js applicatie voor leveranciers om orders te bekijken en bewerken
                      </Descriptions.Item>
                      <Descriptions.Item label="API (Java Spring Boot)">
                        Backend API voor Exact Online integratie en email verzending
                      </Descriptions.Item>
                      <Descriptions.Item label="Firestore">
                        Database voor alle purchase order data, products, suppliers, etc.
                      </Descriptions.Item>
                      <Descriptions.Item label="Cloud Functions">
                        Serverless functions voor automatische email verzending, Exact Online synchronisatie, goods
                        receipt verwerking en voorraad updates. Triggers op: purchaseOrders, purchaseOrderShipments,
                        purchaseOrderInvoices
                      </Descriptions.Item>
                      <Descriptions.Item label="Firebase Storage">
                        Opslag voor invoice PDFs, product images, en order attachments
                      </Descriptions.Item>
                      <Descriptions.Item label="Exact Online">
                        ERP systeem voor financiële administratie en voorraad beheer
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Firestore Collections</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="purchaseOrders">
                        Alle purchase orders met producten, status, leverancier koppeling, etc.
                      </Descriptions.Item>
                      <Descriptions.Item label="purchaseOrderShipments">
                        Verzendingen - losgekoppeld van PO's, kan producten uit meerdere PO's bevatten. Bevat items,
                        receivedQuantities, sourceOrders
                      </Descriptions.Item>
                      <Descriptions.Item label="purchaseOrderInvoices">
                        Facturen - gekoppeld aan een shipment (via shipmentId). Bevat invoice lines, additional costs,
                        status, Exact sync data
                      </Descriptions.Item>
                      <Descriptions.Item label="purchaseOrderPayments">
                        Betalingen - gekoppeld aan een invoice (via invoiceId). Bevat amount, date, method, reference,
                        proof
                      </Descriptions.Item>
                      <Descriptions.Item label="products">Product master data (catalog)</Descriptions.Item>
                      <Descriptions.Item label="suppliers">Leverancier informatie en authenticatie</Descriptions.Item>
                      <Descriptions.Item label="productDrafts">
                        Nieuwe producten die nog niet in de catalog staan (goedkeuringsproces)
                      </Descriptions.Item>
                      <Descriptions.Item label="emailQueue">
                        Queue voor automatische email verzending (verwerkt door Cloud Functions)
                      </Descriptions.Item>
                      <Descriptions.Item label="config">
                        Systeem configuratie (order counter, shipment counter, etc.)
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Data Flow</Title>
                    <Timeline>
                      <Timeline.Item color="blue">
                        <strong>1. Order Creation (CMS)</strong>
                        <br />
                        Bombyxx maakt order aan → Firestore purchaseOrders collection
                      </Timeline.Item>
                      <Timeline.Item color="green">
                        <strong>2. PO Status Change → Cloud Function</strong>
                        <br />
                        Status update → onUpdatePurchaseOrder → Email queue + Exact sync (bij in_production)
                      </Timeline.Item>
                      <Timeline.Item color="orange">
                        <strong>3. Shipment Created (Supplier Portal)</strong>
                        <br />
                        Leverancier maakt verzending aan → purchaseOrderShipments collection
                      </Timeline.Item>
                      <Timeline.Item color="cyan">
                        <strong>4. Shipment Status Change → Cloud Function</strong>
                        <br />
                        Status update → onUpdatePurchaseOrderShipment → Emails + order-status herberekening. De
                        daadwerkelijke Goods Receipt/voorraadtelling naar Exact loopt <em>niet</em> via deze Cloud
                        Function, maar rechtstreeks CMS → Java API (zie sectie 7).
                      </Timeline.Item>
                      <Timeline.Item color="purple">
                        <strong>5. Invoice Created → Cloud Function</strong>
                        <br />
                        Leverancier maakt factuur aan → onCreatePurchaseOrderInvoice → email naar Bombyxx. De Exact-sync
                        die hierbij hoort staat op dit moment bewust stil (zie sectie 3).
                      </Timeline.Item>
                      <Timeline.Item color="green">
                        <strong>6. Invoice Status Change → Cloud Function</strong>
                        <br />
                        Status update (approved/paid) → onUpdatePurchaseOrderInvoice → Email naar leverancier
                      </Timeline.Item>
                      <Timeline.Item color="gray">
                        <strong>7. Email Processing</strong>
                        <br />
                        Cloud Function checkt emailQueue → API MarketingService → Email verzonden
                      </Timeline.Item>
                    </Timeline>
                  </Panel>

                  {/* Troubleshooting */}
                  <Panel
                    header={
                      <Space>
                        <ExclamationCircleOutlined />
                        <strong>10. Troubleshooting & FAQ</strong>
                      </Space>
                    }
                    key="10"
                  >
                    <Title level={4}>Veelvoorkomende Problemen</Title>

                    <Collapse ghost>
                      <Panel header="Email wordt niet verzonden" key="8-1">
                        <Paragraph>
                          <Text strong>Mogelijke oorzaken:</Text>
                        </Paragraph>
                        <ul>
                          <li>Cloud Function is niet actief</li>
                          <li>Email queue entry heeft status 'failed'</li>
                          <li>API MarketingService is down</li>
                          <li>Email adres is incorrect</li>
                        </ul>
                        <Paragraph>
                          <Text strong>Oplossing:</Text>
                        </Paragraph>
                        <ul>
                          <li>Check Firestore emailQueue collection voor status</li>
                          <li>Check Cloud Function logs in Firebase Console</li>
                          <li>Check API logs voor errors</li>
                          <li>Verifieer email adres in supplier/user data</li>
                        </ul>
                      </Panel>

                      <Panel header="Invoice wordt niet gesynchroniseerd naar Exact" key="8-2">
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message="Dit is op dit moment verwacht gedrag, geen storing"
                          description="De Exact-synchronisatie van facturen staat sinds 21-04-2026 bewust uitgeschakeld (zie sectie 3). Voordat je hieronder gaat troubleshooten: check eerst of dit nog steeds de bewuste, huidige situatie is."
                        />
                        <Paragraph>
                          <Text strong>Mocht de sync ooit weer aanstaan, mogelijke oorzaken bij problemen:</Text>
                        </Paragraph>
                        <ul>
                          <li>Exact API credentials zijn verlopen</li>
                          <li>Cloud Function is niet getriggered</li>
                          <li>Invoice data is incompleet</li>
                        </ul>
                        <Paragraph>
                          <Text strong>Oplossing:</Text>
                        </Paragraph>
                        <ul>
                          <li>Check Exact API token in API configuratie</li>
                          <li>Check Cloud Function logs</li>
                          <li>Verifieer invoice heeft alle verplichte velden</li>
                          <li>Check exactPurchaseInvoiceId in Firestore</li>
                        </ul>
                      </Panel>

                      <Panel header="Order splitsen (Split) is niet beschikbaar" key="8-3">
                        <Alert
                          type="info"
                          showIcon
                          message="Dit is geen storing — de functie is nooit afgebouwd"
                          description="Order splitsen stond gepland (vandaar de status-waarde 'Split' en bijbehorende schermteksten die je her en der nog kunt tegenkomen), maar er is geen enkele knop of actie in CMS of Supplier Portal die dit daadwerkelijk uitvoert. Als je op zoek bent naar een manier om een deel van een order apart te verzenden: dat kan wél, via een verzending die niet alle producten van de order bevat (zie sectie 7) — de order zelf hoeft daarvoor niet gesplitst te worden."
                        />
                      </Panel>

                      <Panel header="Product master data wordt niet geüpdatet" key="8-4">
                        <Paragraph>
                          <Text strong>Mogelijke oorzaken:</Text>
                        </Paragraph>
                        <ul>
                          <li>Product heeft geen productId (is een draft product)</li>
                          <li>Waarden zijn niet gewijzigd</li>
                          <li>updateProductPricesAndPackaging functie wordt niet aangeroepen</li>
                        </ul>
                        <Paragraph>
                          <Text strong>Oplossing:</Text>
                        </Paragraph>
                        <ul>
                          <li>Check dat product een productId heeft (geen productDraftId)</li>
                          <li>Verifieer dat adjusted values verschillen van originele values</li>
                          <li>Check CMS logs voor errors in updateProductPricesAndPackaging</li>
                        </ul>
                      </Panel>

                      <Panel header="Additional costs email wordt niet verzonden" key="8-5">
                        <Paragraph>
                          <Text strong>Mogelijke oorzaken:</Text>
                        </Paragraph>
                        <ul>
                          <li>additionalCostsNotificationPending flag is niet gezet</li>
                          <li>Cloud Function onUpdatePurchaseOrder is niet getriggered</li>
                          <li>Email type is niet toegevoegd aan MarketingService</li>
                        </ul>
                        <Paragraph>
                          <Text strong>Oplossing:</Text>
                        </Paragraph>
                        <ul>
                          <li>Check Firestore order voor additionalCostsNotificationPending flag</li>
                          <li>Check Cloud Function logs</li>
                          <li>Verifieer MarketingService heeft 'purchase_order_additional_costs_added' case</li>
                        </ul>
                      </Panel>

                      <Panel header="Voorraadtelling wordt niet verwerkt in Exact" key="8-6">
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message="Dit loopt niet via een Cloud Function"
                          description="De voorraadtelling gaat rechtstreeks van CMS naar de Java API naar Exact (zie sectie 7) op het moment dat je op '🔢 Voorraadtelling verwerken' klikt. Er is dus niets om te 'triggeren' op de achtergrond — als het niet lukt, zie je dat direct als foutmelding in het scherm zelf."
                        />
                        <Paragraph>
                          <Text strong>Mogelijke oorzaken:</Text>
                        </Paragraph>
                        <ul>
                          <li>Shipment heeft geen supplierId of de leverancier heeft geen Exact-koppeling</li>
                          <li>
                            Eén of meer producten zijn niet als SupplierItem gekoppeld aan de leverancier in Exact
                          </li>
                          <li>Exact API credentials zijn verlopen</li>
                        </ul>
                        <Paragraph>
                          <Text strong>Oplossing:</Text>
                        </Paragraph>
                        <ul>
                          <li>
                            Lees de foutmelding in de 'Verwerk Ontvangst' modal — deze toont de Exact-foutmelding direct
                          </li>
                          <li>Check of de leverancier een Exact GUID heeft</li>
                          <li>Check of alle producten in de verzending gekoppeld zijn aan de leverancier in Exact</li>
                          <li>
                            Gebruik de knop 'Exact opnieuw synchroniseren' op de verzending-detailpagina om het opnieuw
                            te proberen
                          </li>
                        </ul>
                      </Panel>

                      <Panel header="Shipment emails worden niet verzonden" key="8-7">
                        <Paragraph>
                          <Text strong>Mogelijke oorzaken:</Text>
                        </Paragraph>
                        <ul>
                          <li>Shipment heeft geen supplierId (supplier lookup faalt)</li>
                          <li>Cloud Function onUpdatePurchaseOrderShipment is niet actief</li>
                          <li>Status change is niet gedetecteerd (before.status === after.status)</li>
                        </ul>
                        <Paragraph>
                          <Text strong>Oplossing:</Text>
                        </Paragraph>
                        <ul>
                          <li>Verifieer supplierId op het shipment document</li>
                          <li>Check Cloud Function deployment status</li>
                          <li>Check Firestore emailQueue voor shipment-gerelateerde entries</li>
                        </ul>
                      </Panel>
                    </Collapse>

                    <Divider />

                    <Title level={4}>Belangrijke Notities</Title>
                    <Alert
                      message="Betaalstatus zit op de factuur, niet op de order"
                      description="Er is geen apart, actief 'betaalstatus'-veld op de inkooporder zelf (dat veld bestaat wel in de data, maar wordt nergens door het CMS gelezen of geschreven). Of iets betaald is, zie je op de factuur/verzending: Submitted → Partially Paid → Paid, op basis van de opgetelde betalingen (zie sectie 4)."
                      type="warning"
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                    <Alert
                      message="Invoice Total Price"
                      description="Total Price in overzichtstabel toont altijd producten + additional costs. In detail pagina wordt breakdown getoond."
                      type="info"
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                    <Alert
                      message="Email Delays"
                      description="Emails worden binnen 1 minuut verzonden na trigger. Check emailQueue collection voor status."
                      type="info"
                      showIcon
                    />
                  </Panel>

                  {/* Automatische Meldingen bij Wijzigingen */}
                  <Panel
                    header={
                      <Space>
                        <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                        <strong>11. Automatische Meldingen bij Inkooporder Wijzigingen</strong>
                      </Space>
                    }
                    key="11"
                  >
                    <Alert
                      message="Wanneer wordt een melding verstuurd?"
                      description="Bij bepaalde wijzigingen in een inkooporder stuurt het systeem automatisch een gestructureerde email naar Bombyxx. Dit geldt voor significante aantalswijzigingen én voor nieuwe orders met producten die al in andere actieve orders staan."
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    {alertNotificationData.map((item, index) => (
                      <Card
                        key={index}
                        size="small"
                        style={{ marginBottom: 16, borderLeft: "4px solid #faad14" }}
                        title={
                          <Space>
                            <Tag color={index === 0 ? "orange" : "red"}>
                              {index === 0 ? "Aantalswijziging" : "Dubbele bestelling"}
                            </Tag>
                            <Text strong>{highlightMatch(item.scenario, searchQuery)}</Text>
                          </Space>
                        }
                      >
                        <Descriptions bordered column={1} size="small">
                          <Descriptions.Item label="Trigger">
                            {highlightMatch(item.trigger, searchQuery)}
                          </Descriptions.Item>
                          <Descriptions.Item label="Drempel">
                            {highlightMatch(item.threshold, searchQuery)}
                          </Descriptions.Item>
                          <Descriptions.Item label="Ontvanger">
                            <Tag color="blue">{item.recipient}</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Email type">
                            <Text code>{item.emailType}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Email template">
                            <Text code>{item.template}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Tabelkolommen in email">
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {item.tableColumns.map((col, i) => (
                                <li key={i}>{highlightMatch(col, searchQuery)}</li>
                              ))}
                            </ul>
                          </Descriptions.Item>
                          <Descriptions.Item label="Extra informatie">
                            {highlightMatch(item.extraInfo, searchQuery)}
                          </Descriptions.Item>
                          <Descriptions.Item label="Activiteitslog">
                            {highlightMatch(item.historyEntry, searchQuery)}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    ))}

                    <Divider />

                    <Title level={4}>Hoe werkt de email queue?</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Systeem">
                        Emails worden <Text strong>niet direct verstuurd</Text> maar in de <Text code>emailQueue</Text>{" "}
                        collectie geplaatst
                      </Descriptions.Item>
                      <Descriptions.Item label="Verwerking">
                        De Java API (MarketingService) verwerkt de queue periodiek — max 3 retries bij failure
                      </Descriptions.Item>
                      <Descriptions.Item label="Template">
                        <Text code>purchase-order-alert.ftl</Text> — een gestructureerde HTML email met badge, intro
                        tekst, overzichtstabel en CMS-knop
                      </Descriptions.Item>
                      <Descriptions.Item label="Delay">
                        Emails worden 5 seconden na de wijziging ingepland (<Text code>scheduledFor = now + 5s</Text>)
                      </Descriptions.Item>
                      <Descriptions.Item label="Controleren">
                        Firestore → emailQueue collectie → filter op{" "}
                        <Text code>type: purchase_order_quantity_changed</Text> of{" "}
                        <Text code>purchase_order_duplicate_warning</Text>
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Voorbeeld: Aantalswijziging email</Title>
                    <Card size="small" style={{ backgroundColor: "#fff7e6", border: "1px solid #ffd591" }}>
                      <Paragraph>
                        <Text strong>Scenario:</Text> Sam wijzigt product "Infinite Large" van 200x naar 50x (−75%)
                      </Paragraph>
                      <Paragraph>
                        <Text strong>Wat er gebeurt:</Text>
                      </Paragraph>
                      <ol>
                        <li>Cloud Function detecteert wijziging ≥ 25%</li>
                        <li>Email wordt aangemaakt in emailQueue met gestructureerde tabeldata</li>
                        <li>API verwerkt de queue en verstuurt een opgemaakte HTML email</li>
                        <li>
                          Email toont: product naam, oud aantal (200x), nieuw aantal (50x), wijziging (−150x, −75%)
                        </li>
                        <li>Knop "Bekijk order in CMS" linkt direct naar de order</li>
                      </ol>
                      <Alert
                        message="Let op: de melding wordt ook verstuurd bij verhogingen ≥ 25%, niet alleen bij verlagingen."
                        type="info"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </Card>

                    <Divider />

                    <Title level={4}>Voorbeeld: Dubbele bestelling email</Title>
                    <Card size="small" style={{ backgroundColor: "#fff1f0", border: "1px solid #ffa39e" }}>
                      <Paragraph>
                        <Text strong>Scenario:</Text> Nieuwe order IO-20260506-100 aangemaakt met "Infinite Large"
                        (12x), maar dit product staat al in IO-20260326-87 (10x), IO-20260413-90 (46x), etc.
                      </Paragraph>
                      <Paragraph>
                        <Text strong>Wat er gebeurt:</Text>
                      </Paragraph>
                      <ol>
                        <li>onCreatePurchaseOrder trigger vergelijkt producten met alle actieve orders</li>
                        <li>Duplikaten worden gegroepeerd per product met totalen</li>
                        <li>
                          Email toont overzichtstabel met: nieuw besteld, per order hoeveel al open staat, onderweg
                          totaal, eindtotaal
                        </li>
                        <li>Melding is informatief — de order wordt gewoon aangemaakt</li>
                      </ol>
                      <Alert
                        message="Dit is een waarschuwing, geen blokkade. De order wordt altijd aangemaakt."
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </Card>
                  </Panel>

                  {/* Activiteitslog / Geschiedenis */}
                  <Panel
                    header={
                      <Space>
                        <ClockCircleOutlined style={{ color: "#1890ff" }} />
                        <strong>12. Activiteitslog (Geschiedenis per inkooporder)</strong>
                      </Space>
                    }
                    key="12"
                  >
                    <Alert
                      message="Wat is de activiteitslog?"
                      description="Elke inkooporder heeft een 'Geschiedenis' sectie onderaan de detailpagina in het CMS. Hier is te zien wie wat wanneer heeft gewijzigd. De log wordt automatisch bijgehouden via Cloud Functions."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Title level={4}>Technische details</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Firestore locatie">
                        <Text code>purchaseOrders/{"{orderId}"}/history</Text> (sub-collectie)
                      </Descriptions.Item>
                      <Descriptions.Item label="Gesorteerd op">
                        <Text code>changedAt desc</Text> — meest recente wijziging bovenaan
                      </Descriptions.Item>
                      <Descriptions.Item label="Realtime updates">
                        Ja — de CMS detailpagina luistert via een Firestore <Text code>onSnapshot</Text> listener
                      </Descriptions.Item>
                      <Descriptions.Item label="Trigger">
                        Cloud Functions: <Text code>onUpdatePurchaseOrder</Text> en{" "}
                        <Text code>onCreatePurchaseOrder</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Locatie in CMS">
                        Inkooporder detailpagina → "Geschiedenis" collapse panel (onderaan de pagina)
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Velden per history entry</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="changedBy">
                        Email van de gebruiker die de wijziging doorvoerde (uit lastModifiedBy.email)
                      </Descriptions.Item>
                      <Descriptions.Item label="changedAt">Unix timestamp van de wijziging</Descriptions.Item>
                      <Descriptions.Item label="changes">
                        Array van gewijzigde velden (zie tabel hieronder)
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Typen wijzigingen</Title>
                    <Table
                      dataSource={historyLogData}
                      columns={[
                        {
                          title: "Type",
                          dataIndex: "changeType",
                          key: "changeType",
                          width: 180,
                          render: (text, record) => (
                            <Tag color="default">
                              <Text code style={{ fontSize: 12 }}>
                                {text}
                              </Text>
                            </Tag>
                          ),
                        },
                        {
                          title: "Label in CMS",
                          dataIndex: "label",
                          key: "label",
                          width: 180,
                          render: (text, record) => (
                            <span style={{ color: record.color, fontWeight: 500 }}>{text}</span>
                          ),
                        },
                        {
                          title: "Wanneer",
                          dataIndex: "description",
                          key: "description",
                          render: (text) => highlightMatch(text, searchQuery),
                        },
                        {
                          title: "Extra velden",
                          dataIndex: "fields",
                          key: "fields",
                          render: (fields) => (
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {fields.map((f, i) => (
                                <li key={i}>
                                  <Text code style={{ fontSize: 11 }}>
                                    {f}
                                  </Text>
                                </li>
                              ))}
                            </ul>
                          ),
                        },
                      ]}
                      pagination={false}
                      rowKey="changeType"
                      size="small"
                      bordered
                    />

                    <Divider />

                    <Title level={4}>Wie wordt er als auteur geregistreerd?</Title>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Bij statuswijziging (CMS)">
                        Het email-adres van de ingelogde Bombyxx gebruiker — opgeslagen als{" "}
                        <Text code>lastModifiedBy.email</Text> op de order bij elke update vanuit het CMS
                      </Descriptions.Item>
                      <Descriptions.Item label="Bij order aanmaken">
                        <Text code>order.createdBy</Text> of als fallback <Text code>order.lastModifiedBy.email</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Bij onbekende gebruiker">
                        Valt terug op de string <Text code>"onbekend"</Text>
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Voorbeeld history entry (Firestore)</Title>
                    <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
                      <pre style={{ fontSize: 12, margin: 0 }}>{`{
  changedBy: "sam@bombyxx.com",
  changedAt: 1746537600,
  changes: [
    {
      field: "status",
      oldValue: "pending_confirmation",
      newValue: "ready_for_production"
    },
    {
      field: "product_amount",
      productId: "abc123",
      productName: "Infinite Large",
      productCode: "BG21103",
      oldValue: 200,
      newValue: 50
    }
  ]
}`}</pre>
                    </Card>

                    <Divider />

                    <Alert
                      message="Firestore index vereist"
                      description={
                        <span>
                          De history sub-collectie gebruikt <Text code>orderBy("changedAt", "desc")</Text>. Firestore
                          maakt deze index automatisch aan bij de eerste query. Tot die tijd laadt de geschiedenis leeg
                          (geen foutmelding). Dit duurt doorgaans minder dan 1 minuut.
                        </span>
                      }
                      type="warning"
                      showIcon
                    />
                  </Panel>
                </Collapse>
              </>
            ),
          },
          {
            key: "marketing",
            label: (
              <span>
                <MailOutlined /> Marketing
              </span>
            ),
            children: (
              <>
                <Paragraph>Documentatie voor marketing email campagnes en tijdelijke klant instellingen.</Paragraph>
                <Collapse activeKey={activeKey} onChange={setActiveKey}>
                  {/* Marketing Emails */}
                  <Panel
                    header={
                      <Space>
                        <MailOutlined />
                        <strong>1. Marketing Email Campagnes</strong>
                      </Space>
                    }
                    key="marketing-1"
                  >
                    <Alert
                      message="Dynamische Marketing Campagnes"
                      description="Verstuur gepersonaliseerde marketing emails naar klanten met tijdelijke instellingen die automatisch verlopen aan het einde van de dag."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Title level={4}>Beschikbare Campagne Types</Title>
                    <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                      <Descriptions.Item label="🎁 Gratis Verzending € 1000">
                        Alleen vandaag gratis verzending bij bestellingen vanaf € 1000
                      </Descriptions.Item>
                      <Descriptions.Item label="💰 Bestellen vanaf € 500">
                        Alleen vandaag bestellen vanaf €500 (verlaagde minimale bestelwaarde)
                      </Descriptions.Item>
                      <Descriptions.Item label="🎉 5% Korting">
                        Alleen vandaag 5% korting bij bestellingen boven [dynamisch bedrag]
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Hoe te Gebruiken</Title>
                    <Timeline>
                      <Timeline.Item color="blue">
                        <Title level={5}>1. Open Customer Drawer</Title>
                        <Paragraph>
                          Ga naar Klanten pagina en klik op een klant om de Customer Drawer te openen
                        </Paragraph>
                      </Timeline.Item>
                      <Timeline.Item color="blue">
                        <Title level={5}>2. Klik op "Verstuur Marketing Email"</Title>
                        <Paragraph>
                          In de Customer Drawer, scroll naar de "Marketing Emails" sectie en klik op de knop
                        </Paragraph>
                      </Timeline.Item>
                      <Timeline.Item color="blue">
                        <Title level={5}>3. Selecteer Campagne Type</Title>
                        <Paragraph>Kies een van de 3 beschikbare campagne types. De modal toont automatisch:</Paragraph>
                        <ul>
                          <li>Huidige winkelwagen waarde van de klant</li>
                          <li>Berekende drempel voor 5% korting campagne (afgerond naar eerstvolgende duizendtal)</li>
                          <li>Waarschuwing als dezelfde email recent is verstuurd (&lt; 7 dagen)</li>
                        </ul>
                      </Timeline.Item>
                      <Timeline.Item color="blue">
                        <Title level={5}>4. Verstuur Email</Title>
                        <Paragraph>
                          Klik op "Versturen" om de email te verzenden. De email wordt direct verstuurd en:
                        </Paragraph>
                        <ul>
                          <li>Tijdelijke instellingen worden geactiveerd voor de klant</li>
                          <li>Email wordt opgeslagen in de historiek</li>
                          <li>Instellingen verlopen automatisch aan het einde van de dag</li>
                        </ul>
                      </Timeline.Item>
                    </Timeline>

                    <Divider />

                    <Title level={4}>Dynamische Drempel Berekening (5% Korting)</Title>
                    <Card size="small" style={{ backgroundColor: "#f0f5ff", marginBottom: 16 }}>
                      <Paragraph>
                        <Text strong>Automatische Berekening:</Text>
                      </Paragraph>
                      <ul>
                        <li>Winkelwagen: €3.300 → Drempel: €4.000</li>
                        <li>Winkelwagen: €2.800 → Drempel: €3.000</li>
                        <li>Winkelwagen: €5.100 → Drempel: €6.000</li>
                      </ul>
                      <Paragraph>
                        De drempel wordt automatisch afgerond naar het eerstvolgende duizendtal om een psychologisch
                        aantrekkelijk bedrag te creëren.
                      </Paragraph>
                    </Card>

                    <Divider />

                    <Title level={4}>Tijdelijke Instellingen</Title>
                    <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                      <Descriptions.Item label="Gratis Verzending €750">
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>freeShippingUntil: einde van vandaag</li>
                          <li>freeShippingThreshold: €750</li>
                        </ul>
                      </Descriptions.Item>
                      <Descriptions.Item label="Bestellen vanaf €500">
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>minimumOrderOverrideUntil: einde van vandaag</li>
                          <li>minimumOrderAmount: €500</li>
                        </ul>
                      </Descriptions.Item>
                      <Descriptions.Item label="5% Korting">
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>discountPercentageUntil: einde van vandaag</li>
                          <li>discountPercentage: 5%</li>
                          <li>discountMinimumAmount: [berekende drempel]</li>
                        </ul>
                      </Descriptions.Item>
                    </Descriptions>

                    <Alert
                      message="Automatisch Verlopen"
                      description="Alle tijdelijke instellingen verlopen automatisch aan het einde van de dag (23:59:59). Er is geen handmatige actie nodig."
                      type="success"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Divider />

                    <Title level={4}>Email Historiek</Title>
                    <Paragraph>
                      In de Customer Drawer wordt een overzicht getoond van alle verstuurde marketing emails:
                    </Paragraph>
                    <ul>
                      <li>Type campagne met icoon en kleur</li>
                      <li>Datum en tijd van verzending</li>
                      <li>Winkelwagen waarde op moment van verzenden</li>
                      <li>Berekende drempel (voor 5% korting campagne)</li>
                      <li>Waarschuwing als email recent is verstuurd (&lt; 7 dagen geleden)</li>
                    </ul>

                    <Divider />

                    <Title level={4}>Database Structuur</Title>
                    <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                      <Descriptions.Item label="Email Historiek">
                        /users/{"{userId}"}/marketingEmails/{"{emailId}"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tijdelijke Instellingen">
                        /users/{"{userId}"} → temporarySettings object
                      </Descriptions.Item>
                      <Descriptions.Item label="Email Queue">
                        Gebruikt bestaande emailQueue collectie voor verzending
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Title level={4}>Webshop Integratie</Title>
                    <Alert
                      message="Checkout Validatie"
                      description="De webshop checkout moet de temporarySettings van de gebruiker controleren en de juiste kortingen/gratis verzending toepassen."
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Paragraph>
                      <Text strong>Te implementeren in checkout:</Text>
                    </Paragraph>
                    <ul>
                      <li>Check user.temporarySettings bij het laden van checkout</li>
                      <li>Valideer expiresAt timestamp (moet in de toekomst liggen)</li>
                      <li>Toon banner met actieve aanbieding</li>
                      <li>Pas gratis verzending toe als freeShippingUntil actief is</li>
                      <li>Verlaag minimum order amount als minimumOrderOverrideUntil actief is</li>
                      <li>Pas korting toe als discountPercentageUntil actief is en order boven drempel</li>
                    </ul>

                    <Divider />

                    <Title level={4}>Best Practices</Title>
                    <Alert
                      message="Frequentie Waarschuwing"
                      description="Het systeem waarschuwt automatisch als dezelfde email binnen 7 dagen is verstuurd. Dit voorkomt email fatigue bij klanten."
                      type="info"
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                    <Alert
                      message="Rate Limiting"
                      description="Er is een delay van 40 seconden tussen het versturen van emails om email server limits te respecteren (max 10 emails per 5 minuten)."
                      type="info"
                      showIcon
                    />
                  </Panel>
                </Collapse>
              </>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default Documentation;
